// Shared helpers for the productivity adjustment matrix (damage combination ×
// access method). Used by the Mobilization Costs → Productivity
// Adjustments page and by the inline panel in the Access Methods modal so
// both render and validate the same way.

// Valid range is [1, 100]: a leading zero is never accepted, so the smallest
// value that can be typed is 1, and 100 means "no effect on base productivity".
export const MIN_ADJUSTMENT_PERCENT = 1;
export const MAX_ADJUSTMENT_PERCENT = 100;
export const NO_EFFECT_ADJUSTMENT_PERCENT = 100;

// Must start 1-9, so "0", "01" and "0000" are all rejected outright; "10" and
// "12.5" are fine. Up to two decimals, matching how values are stored.
const ADJUSTMENT_INPUT_PATTERN = /^[1-9]\d*(\.\d{0,2})?$/;

/**
 * Whether a raw input string may be typed into an adjustment field. Empty is
 * allowed so the field can be cleared and retyped; it is resolved on blur.
 */
export const isAllowedAdjustmentText = (text) => {
  if (text === "" || text === null || text === undefined) return true;
  const value = String(text);
  if (!ADJUSTMENT_INPUT_PATTERN.test(value)) return false;
  return Number(value) <= MAX_ADJUSTMENT_PERCENT;
};

export const isValidAdjustmentPercent = (value) => {
  if (value === "" || value === null || value === undefined) return false;
  const parsed = Number(value);
  return (
    Number.isFinite(parsed) &&
    parsed >= MIN_ADJUSTMENT_PERCENT &&
    parsed <= MAX_ADJUSTMENT_PERCENT
  );
};

export const normalizeAdjustmentPercent = (value) => {
  if (!isValidAdjustmentPercent(value)) return null;
  return Math.round(Number(value) * 100) / 100;
};

export const getAdjustmentKey = (row) =>
  `${Number(row?.corrosionDamageCombinationId || 0)}|${Number(
    row?.accessMethodSystemListId || 0,
  )}`;

/**
 * Pivot flat adjustment rows into a compact grid: one block per application
 * method, one row per exposure class (C2, C3, ...), one column per damage
 * severity (A, B, C).
 *
 * @param rows            adjustment rows from the API matrix
 * @param methodSortIndex optional Map<methodId(string), number> giving the
 *                        preferred method order (defaults to name order)
 * @returns { severities, groups }
 */
export const buildAdjustmentGrid = (rows = [], methodSortIndex = null) => {
  const severityByCode = new Map();
  const groupByMethodId = new Map();

  rows.forEach((row) => {
    const combinationCode = String(row.combinationCode || "").trim();
    const severityCode =
      String(row.damageSeverityCode || "").trim() ||
      combinationCode.slice(-1) ||
      "—";
    const classCode =
      String(row.exposureClassCode || "").trim() ||
      combinationCode.slice(0, -1) ||
      "—";

    if (!severityByCode.has(severityCode)) {
      severityByCode.set(severityCode, {
        code: severityCode,
        name: row.damageSeverityName || "",
        sortOrder: Number(row.damageSeverityId || 0),
      });
    }

    const methodId = String(row.accessMethodSystemListId || "");
    if (!groupByMethodId.has(methodId)) {
      groupByMethodId.set(methodId, {
        methodId,
        methodName: row.accessMethodName || "",
        classOrder: [],
        classByCode: new Map(),
      });
    }

    const group = groupByMethodId.get(methodId);
    if (!group.classByCode.has(classCode)) {
      group.classByCode.set(classCode, {
        classCode,
        className: row.exposureClassName || "",
        sortOrder: Number(row.corrosionCategoryId || 0),
        rowBySeverityCode: new Map(),
      });
      group.classOrder.push(classCode);
    }
    group.classByCode.get(classCode).rowBySeverityCode.set(severityCode, row);
  });

  const severities = [...severityByCode.values()].sort(
    (left, right) =>
      left.sortOrder - right.sortOrder || left.code.localeCompare(right.code),
  );

  const groups = [...groupByMethodId.values()]
    .sort((left, right) => {
      if (methodSortIndex) {
        const diff =
          (methodSortIndex.get(left.methodId) ?? 999) -
          (methodSortIndex.get(right.methodId) ?? 999);
        if (diff !== 0) return diff;
      }
      return left.methodName.localeCompare(right.methodName);
    })
    .map((group) => ({
      methodId: group.methodId,
      methodName: group.methodName,
      rows: group.classOrder
        .map((classCode) => group.classByCode.get(classCode))
        .sort(
          (left, right) =>
            left.sortOrder - right.sortOrder ||
            left.classCode.localeCompare(right.classCode),
        )
        .map((classRow) => ({
          classCode: classRow.classCode,
          className: classRow.className,
          cells: severities.map((severity) => ({
            severityCode: severity.code,
            row: classRow.rowBySeverityCode.get(severity.code) || null,
          })),
        })),
    }));

  return { severities, groups };
};
